import { Check, Monitor, Moon, RotateCcw, Sun, X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import {
  MATIZ_PADRAO,
  useAparencia,
  type Cantos,
  type Densidade,
  type Fonte,
  type Tema,
} from '../lib/aparencia';
import { Botao } from './ui';

const TEMAS: { valor: Tema; rotulo: string; Icone: typeof Sun }[] = [
  { valor: 'claro', rotulo: 'Claro', Icone: Sun },
  { valor: 'escuro', rotulo: 'Escuro', Icone: Moon },
  { valor: 'automatico', rotulo: 'Automático', Icone: Monitor },
];

/**
 * Matizes oferecidas.
 *
 * São matizes (0–360), não códigos hexadecimais: a cor final é calculada em
 * oklch a partir dela. Uma paleta de hexadecimais exigiria manter duas
 * versões de cada cor à mão, e alguém acabaria escolhendo uma que some no
 * escuro.
 *
 * O círculo de cada opção usa L=0.565 (o L de `--color-marca-600` no tema
 * claro, ver `estilos.css`) — pior caso medido 3,87:1 no claro e 3,71:1 no
 * escuro contra o check (`text-marca-contraste`), ambos acima do mínimo
 * 3:1 do WCAG 2.1 SC 1.4.11. Mexer nesse L sem remedir reprova o check.
 */
/*
  Doze matizes, na volta completa do círculo, do jeito do isotec-react.

  Eram oito e o dono pediu mais opções e cores mais vivas. O croma subiu de
  0.16 para 0.25 em `--pri-c` — e escurecer junto (`marca-600` de L 0.52 para
  0.48) fez o pior matiz IR DE 4.62 para 4.75:1 contra o branco. Mais vivo e
  com MAIS contraste ao mesmo tempo, o que é contraintuitivo e foi medido.

  A ordem segue o círculo cromático: quem procura "um verde" acha os verdes
  juntos.
*/
const MATIZES: { valor: number; nome: string }[] = [
  { valor: 25, nome: 'Vermelho' },
  { valor: 55, nome: 'Laranja' },
  { valor: 85, nome: 'Âmbar' },
  { valor: 120, nome: 'Lima' },
  { valor: 145, nome: 'Verde' },
  { valor: 170, nome: 'Esmeralda' },
  { valor: 195, nome: 'Turquesa' },
  { valor: 225, nome: 'Ciano' },
  { valor: MATIZ_PADRAO, nome: 'Azul Specializer' },
  { valor: 285, nome: 'Índigo' },
  { valor: 320, nome: 'Violeta' },
  { valor: 345, nome: 'Rosa' },
];

const FONTES: { valor: Fonte; rotulo: string }[] = [
  { valor: 'pequena', rotulo: 'Pequena' },
  { valor: 'media', rotulo: 'Média' },
  { valor: 'grande', rotulo: 'Grande' },
];

const DENSIDADES: { valor: Densidade; rotulo: string }[] = [
  { valor: 'compacta', rotulo: 'Compacta' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'confortavel', rotulo: 'Confortável' },
];

const CANTOS: { valor: Cantos; rotulo: string }[] = [
  { valor: 'reto', rotulo: 'Retos' },
  { valor: 'medio', rotulo: 'Médios' },
  { valor: 'redondo', rotulo: 'Redondos' },
];

function Secao({
  titulo,
  dica,
  children,
}: {
  titulo: string;
  dica?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-tinta-900 text-sm font-semibold">{titulo}</h3>
        {dica && <p className="text-tinta-500 text-xs">{dica}</p>}
      </div>
      {children}
    </section>
  );
}

/** Grupo de botões exclusivos, no estilo do seletor de tema. */
function Grupo<T extends string>({
  valor,
  opcoes,
  aoEscolher,
  rotuloGrupo,
}: {
  valor: T;
  opcoes: { valor: T; rotulo: string }[];
  aoEscolher: (v: T) => void;
  rotuloGrupo: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={rotuloGrupo}
      className="bg-tinta-100 flex gap-0.5 rounded-xl p-0.5"
    >
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={valor === o.valor}
          onClick={() => aoEscolher(o.valor)}
          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            valor === o.valor
              ? 'bg-superficie text-tinta-900 shadow-sm'
              : 'text-tinta-500 hover:text-tinta-700'
          }`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/**
 * Painel de aparência.
 *
 * Gaveta lateral com tudo que muda a cara do sistema. Fica fora do fluxo das
 * telas de propósito: é configuração pessoal, não é uma tela do sistema, e
 * quem abre quer voltar para onde estava.
 *
 * A escolha é por USUÁRIO no navegador, não por tenant. Duas pessoas no mesmo
 * escritório podem preferir densidades diferentes, e uma delas usa o tablet.
 */
export function PainelAparencia({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}) {
  const a = useAparencia();

  // Esc fecha: quem abriu por engano não deve ter de procurar o X.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  return (
    <>
      <div
        className="bg-palco/40 fixed inset-0 z-40 backdrop-blur-[2px]"
        onClick={aoFechar}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label="Aparência do sistema"
        aria-modal="true"
        className="bg-superficie border-tinta-100 animate-surgir fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l shadow-2xl"
      >
        <header className="border-tinta-100 flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-tinta-900 font-semibold">Aparência</h2>
            <p className="text-tinta-500 text-xs">Vale só para você, neste navegador.</p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="text-tinta-500 hover:text-tinta-900 hover:bg-marca-600/10 rounded-lg p-1.5 transition-colors"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="rolagem-fina flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 py-5">
          <Secao titulo="Tema">
            <div
              role="radiogroup"
              aria-label="Tema"
              className="bg-tinta-100 flex gap-0.5 rounded-xl p-0.5"
            >
              {TEMAS.map(({ valor, rotulo, Icone }) => (
                <button
                  key={valor}
                  type="button"
                  role="radio"
                  aria-checked={a.tema === valor}
                  onClick={() => a.definirTema(valor)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-medium transition-colors ${
                    a.tema === valor
                      ? 'bg-superficie text-tinta-900 shadow-sm'
                      : 'text-tinta-500 hover:text-tinta-700'
                  }`}
                >
                  <Icone className="size-4" />
                  {rotulo}
                </button>
              ))}
            </div>
          </Secao>

          <Secao
            titulo="Cor do sistema"
            dica="Muda botão, link, foco e destaque de uma vez."
          >
            <div className="grid grid-cols-6 gap-2">
              {MATIZES.map((m) => (
                <button
                  key={m.valor}
                  type="button"
                  title={m.nome}
                  aria-label={m.nome}
                  aria-pressed={a.matiz === m.valor}
                  onClick={() => a.definir({ matiz: m.valor })}
                  // O MESMO L de `--color-marca-600` no tema claro: o círculo
                  // mostra a cor exata que o botão vai ter, não uma aproximação.
                  style={{ background: `oklch(0.48 var(--pri-c) ${m.valor})` }}
                  className={`flex aspect-square items-center justify-center rounded-full transition-transform hover:scale-110 ${
                    a.matiz === m.valor
                      ? 'ring-tinta-900 ring-2 ring-offset-2 ring-offset-[var(--color-superficie)]'
                      : ''
                  }`}
                >
                  {a.matiz === m.valor && (
                    <Check
                      className="text-marca-contraste size-3.5 drop-shadow"
                      strokeWidth={3}
                    />
                  )}
                </button>
              ))}
            </div>
            <label className="block pt-1">
              <span className="text-tinta-500 text-xs">Ajuste fino</span>
              <input
                type="range"
                min={0}
                max={360}
                value={a.matiz}
                onChange={(e) => a.definir({ matiz: Number(e.target.value) })}
                aria-label="Matiz da cor do sistema"
                className="accent-marca-600 mt-1 w-full"
              />
            </label>
          </Secao>

          <Secao titulo="Tamanho do texto" dica="Vale para o sistema inteiro.">
            <Grupo
              rotuloGrupo="Tamanho do texto"
              valor={a.fonte}
              opcoes={FONTES}
              aoEscolher={(fonte) => a.definir({ fonte })}
            />
          </Secao>

          <Secao
            titulo="Densidade das listas"
            dica="Compacta cabe mais linha; confortável é melhor no tablet."
          >
            <Grupo
              rotuloGrupo="Densidade"
              valor={a.densidade}
              opcoes={DENSIDADES}
              aoEscolher={(densidade) => a.definir({ densidade })}
            />
          </Secao>

          <Secao titulo="Cantos">
            <Grupo
              rotuloGrupo="Cantos"
              valor={a.cantos}
              opcoes={CANTOS}
              aoEscolher={(cantos) => a.definir({ cantos })}
            />
          </Secao>

          <Secao titulo="Acessibilidade">
            <label className="hover:bg-marca-600/10 flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors">
              <input
                type="checkbox"
                checked={a.altoContraste}
                onChange={(e) => a.definir({ altoContraste: e.target.checked })}
                className="accent-marca-600 mt-0.5 size-4"
              />
              <span className="text-sm">
                <span className="text-tinta-900 block font-medium">Alto contraste</span>
                <span className="text-tinta-500 text-xs">
                  Escurece textos de apoio e reforça bordas.
                </span>
              </span>
            </label>
            <label className="hover:bg-marca-600/10 flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors">
              <input
                type="checkbox"
                checked={a.menosMovimento}
                onChange={(e) => a.definir({ menosMovimento: e.target.checked })}
                className="accent-marca-600 mt-0.5 size-4"
              />
              <span className="text-sm">
                <span className="text-tinta-900 block font-medium">Menos movimento</span>
                <span className="text-tinta-500 text-xs">
                  Corta animações e transições.
                </span>
              </span>
            </label>
          </Secao>
        </div>

        <footer className="border-tinta-100 border-t px-5 py-4">
          <Botao variante="fantasma" onClick={a.restaurar} className="w-full">
            <RotateCcw className="size-4" />
            Voltar ao padrão
          </Botao>
        </footer>
      </aside>
    </>
  );
}
